/**
 * inoParser.ts
 * ESP8266ファームウェア(.inoファイル群)をブラウザ上でパースして
 * ゲームテキストやAPI設定を抽出する。
 */

export interface ParsedInoData {
    texts: string[]; // loadTextdump()の順序でテキスト配列 (JP優先)
    textsJp: string[]; // 日本語テキスト
    textsEn: string[]; // 英語テキスト
    apiUrl: string | null;
    apiToken: string | null;
    filesDetected: string[]; // 認識できたファイル名
    tiles: Uint8Array[]; // td0..tdN PROGMEM配列 (タイルデータ)
    maps: Uint8Array[]; // md0..mdN PROGMEM配列 (マップデータ)
    music: Uint8Array[]; // sd0..sdN PROGMEM配列 (音楽データ)
}

const EMPTY: ParsedInoData = {
    texts: [],
    textsJp: [],
    textsEn: [],
    apiUrl: null,
    apiToken: null,
    filesDetected: [],
    tiles: [],
    maps: [],
    music: [],
};

const MAX_DAYDREAM_TOTAL_SIZE = 16 * 1024 * 1024;
const MAX_INO_FILE_COUNT = 64;
const MAX_INDEXED_ENTRY_COUNT = 4096;
const MAX_TEXT_ENTRY_LENGTH = 1024 * 1024;
const MAX_BINARY_ENTRY_LENGTH = 4 * 1024 * 1024;

function parseBoundedIndex(rawIndex: string, label: string): number {
    const index = Number(rawIndex);
    if (
        !Number.isSafeInteger(index) ||
        index < 0 ||
        index >= MAX_INDEXED_ENTRY_COUNT
    ) {
        throw new Error(
            `${label} index exceeds the Daydream limit (${MAX_INDEXED_ENTRY_COUNT - 1}): ${rawIndex}`,
        );
    }
    return index;
}

/**
 * PROGMEM文字列 text_jp_N / text_en_N を抽出する。
 * 例: const char text_jp_0[] PROGMEM = "...";
 *     const char PROGMEM text_jp_0[] = "...";
 */
function extractTextList(src: string): { jp: string[]; en: string[] } {
    const jp: Record<number, string> = {};
    const en: Record<number, string> = {};

    // パターン1: const char text_jp_N[] PROGMEM = "..."
    // パターン2: const char PROGMEM text_jp_N[] = "..."
    // 複数行文字列やエスケープシーケンスを含む場合も対応
    const re =
        /const\s+char\s+(?:PROGMEM\s+)?text_(jp|en)_(\d+)\s*\[\s*\]\s*(?:PROGMEM\s*)?=\s*"((?:[^"\\]|\\.)*)"\s*;/gs;
    let m: RegExpExecArray | null;
    while ((m = re.exec(src)) !== null) {
        const lang = m[1];
        const idx = parseBoundedIndex(m[2], "Text");
        if (m[3].length > MAX_TEXT_ENTRY_LENGTH) {
            throw new Error(
                `Text entry length exceeds the Daydream limit (${MAX_TEXT_ENTRY_LENGTH} characters)`,
            );
        }
        // Cエスケープシーケンスをデコード
        const text = decodeCString(m[3]);
        if (lang === "jp") jp[idx] = text;
        else en[idx] = text;
    }

    const jpArr = buildIndexedArray(jp);
    const enArr = buildIndexedArray(en);
    return { jp: jpArr, en: enArr };
}

function buildIndexedArray(map: Record<number, string>): string[] {
    const keys = Object.keys(map)
        .map(Number)
        .sort((a, b) => a - b);
    if (keys.length === 0) return [];
    const maxIdx = keys[keys.length - 1];
    const arr: string[] = [];
    for (let i = 0; i <= maxIdx; i++) {
        arr.push(map[i] ?? "");
    }
    return arr;
}

/**
 * C文字列のエスケープシーケンスをデコードする。
 */
function decodeCString(s: string): string {
    return s
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, "\t")
        .replace(/\\r/g, "\r")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, "\\")
        .replace(/\\x([0-9A-Fa-f]{2})/g, (_, h) =>
            String.fromCharCode(parseInt(h, 16)),
        )
        .replace(/\\([0-7]{1,3})/g, (_, o) =>
            String.fromCharCode(parseInt(o, 8)),
        );
}

/**
 * main.inoからAPIトークン/URLを抽出する。
 */
function extractApiConfig(src: string): {
    apiUrl: string | null;
    apiToken: string | null;
} {
    const urlMatch = src.match(/const\s+char\s*\*\s*apiUrl\s*=\s*"([^"]+)"/);
    const tokenMatch = src.match(
        /const\s+char\s*\*\s*apiToken\s*=\s*"([^"]+)"/,
    );
    return {
        apiUrl: urlMatch ? urlMatch[1] : null,
        apiToken: tokenMatch ? tokenMatch[1] : null,
    };
}

/**
 * C言語バイト配列文字列から Uint8Array を生成する。
 * "0x00, 0xFF, ..." 形式を解析する。
 */
function parseCByteArray(str: string): Uint8Array {
    const bytes: number[] = [];
    const re = /0[xX]([0-9A-Fa-f]{1,2})/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(str)) !== null) {
        if (bytes.length >= MAX_BINARY_ENTRY_LENGTH) {
            throw new Error(
                `Binary entry length exceeds the Daydream limit (${MAX_BINARY_ENTRY_LENGTH} bytes)`,
            );
        }
        bytes.push(parseInt(m[1], 16));
    }
    return new Uint8Array(bytes);
}

function buildIndexedByteArrays(map: Record<number, Uint8Array>): Uint8Array[] {
    const keys = Object.keys(map)
        .map(Number)
        .sort((a, b) => a - b);
    if (keys.length === 0) return [];
    const maxIdx = keys[keys.length - 1];
    const arr: Uint8Array[] = [];
    for (let i = 0; i <= maxIdx; i++) {
        arr.push(map[i] ?? new Uint8Array(0));
    }
    return arr;
}

/**
 * textList.inoのtdN/mdN/sdN PROGMEM配列を抽出する。
 * uint8_t td0[] PROGMEM = { ... };
 */
function extractBinaryData(src: string): {
    tiles: Uint8Array[];
    maps: Uint8Array[];
    music: Uint8Array[];
} {
    const tiles: Record<number, Uint8Array> = {};
    const maps: Record<number, Uint8Array> = {};
    const music: Record<number, Uint8Array> = {};

    // "= { ... }" または "= //コメント\n{ ... }" の両形式に対応
    const re =
        /uint8_t\s+(td|md|sd)(\d+)\s*\[\s*\]\s*PROGMEM\s*=\s*(?:\/\/[^\n]*)?\s*\{([^}]*)\}\s*;/gs;
    let m: RegExpExecArray | null;
    while ((m = re.exec(src)) !== null) {
        const prefix = m[1];
        const idx = parseBoundedIndex(m[2], "Binary");
        const bytes = parseCByteArray(m[3]);
        if (prefix === "td") tiles[idx] = bytes;
        else if (prefix === "md") maps[idx] = bytes;
        else if (prefix === "sd") music[idx] = bytes;
    }

    return {
        tiles: buildIndexedByteArrays(tiles),
        maps: buildIndexedByteArrays(maps),
        music: buildIndexedByteArrays(music),
    };
}

/**
 * ファイル名からinoの種類を推定する。
 */
function detectInoType(
    filename: string,
): "textList" | "main" | "textEngine" | "other" {
    const lower = filename.toLowerCase();
    if (lower.includes("textlist") || lower.includes("text_list"))
        return "textList";
    if (lower === "main.ino") return "main";
    if (lower.includes("textengine") || lower.includes("text_engine"))
        return "textEngine";
    return "other";
}

/**
 * 複数の.inoファイルをパースして統合する。
 */
export async function parseInoFiles(files: File[]): Promise<ParsedInoData> {
    if (files.length > MAX_INO_FILE_COUNT) {
        throw new Error(
            `INO file count exceeds the Daydream limit (${MAX_INO_FILE_COUNT})`,
        );
    }

    let declaredTotalSize = 0;
    for (const file of files) {
        if (!Number.isSafeInteger(file.size) || file.size < 0) {
            throw new Error(`Invalid INO file size for "${file.name}"`);
        }
        if (file.size > MAX_DAYDREAM_TOTAL_SIZE - declaredTotalSize) {
            throw new Error(
                `INO total size exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} bytes)`,
            );
        }
        declaredTotalSize += file.size;
    }

    const result: ParsedInoData = {
        ...EMPTY,
        filesDetected: [],
        texts: [],
        textsJp: [],
        textsEn: [],
    };

    let sourceCharacterCount = 0;

    for (const file of files) {
        const src = await file.text();
        if (src.length > MAX_DAYDREAM_TOTAL_SIZE - sourceCharacterCount) {
            throw new Error(
                `INO total size exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} characters)`,
            );
        }
        sourceCharacterCount += src.length;
        const inoType = detectInoType(file.name);

        if (inoType === "textList") {
            const { jp, en } = extractTextList(src);
            if (jp.length > 0 || en.length > 0) {
                result.textsJp = jp;
                result.textsEn = en;
                result.filesDetected.push(file.name);
            }
            const { tiles, maps, music } = extractBinaryData(src);
            if (tiles.length > 0) result.tiles = tiles;
            if (maps.length > 0) result.maps = maps;
            if (music.length > 0) result.music = music;
        } else if (inoType === "main") {
            const { apiUrl, apiToken } = extractApiConfig(src);
            if (apiUrl) result.apiUrl = apiUrl;
            if (apiToken) result.apiToken = apiToken;
            result.filesDetected.push(file.name);
        } else if (inoType === "textEngine") {
            result.filesDetected.push(file.name);
        } else {
            // mainっぽい内容かもチェック
            const { apiUrl, apiToken } = extractApiConfig(src);
            if (apiUrl || apiToken) {
                if (apiUrl) result.apiUrl = apiUrl;
                if (apiToken) result.apiToken = apiToken;
                result.filesDetected.push(file.name);
            }
            // textListっぽい内容もチェック
            const { jp, en } = extractTextList(src);
            if (jp.length > 0 || en.length > 0) {
                result.textsJp = jp;
                result.textsEn = en;
                result.filesDetected.push(file.name);
            }
            const { tiles, maps, music } = extractBinaryData(src);
            if (tiles.length > 0) result.tiles = tiles;
            if (maps.length > 0) result.maps = maps;
            if (music.length > 0) result.music = music;
        }
    }

    // textsはJP優先（JPがなければEN）
    const maxLen = Math.max(result.textsJp.length, result.textsEn.length);
    for (let i = 0; i < maxLen; i++) {
        result.texts.push(result.textsJp[i] || result.textsEn[i] || "");
    }

    return result;
}
